import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Link,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Container,
  Stack,
  InputAdornment,
  alpha,
  Slide,
  TablePagination
} from '@mui/material';
import {
  Storefront as StorefrontIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserSale } from '../../types/user';
import { salesService } from '../../services/api';

const MySales: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ searchParams ] = useSearchParams();
  const userId = searchParams.get( 'userId' );

  const [ searchTerm, setSearchTerm ] = useState( '' );
  const [ statusFilter, setStatusFilter ] = useState( 'all' );
  const [ sales, setSales ] = useState<UserSale[]>( [] );
  const [ loading, setLoading ] = useState( true );
  const [ error, setError ] = useState<string | null>( null );
  const [ toast, setToast ] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>( {
    open: false,
    message: '',
    severity: 'success'
  } );
  const [ page, setPage ] = useState( 0 );        // ✅ 0-based
  const [ rowsPerPage, setRowsPerPage ] = useState( 10 );
  const [ totalPages, setTotalPages ] = useState( 0 ); // ✅ backend total count

  useEffect( () => {
    if ( !userId ) return;

    // ✅ 1–2 letters par API call band
    if ( searchTerm.length > 0 && searchTerm.length < 3 ) {
      return;
    }

    fetchSales();
  }, [ userId, page, searchTerm, statusFilter ] );

  const fetchSales = async () => {
    if ( !userId ) return;

    try {
      setLoading( true );
      setError( null );
      const response = await salesService.getUserSales( userId, {
        page: page + 1,                 // ✅ UI 0-based → Backend 1-based
        limit: rowsPerPage,
        search: searchTerm.length >= 3 ? searchTerm : "",
        status: statusFilter !== "all" ? statusFilter : ""
      } );

      if ( response.success ) {
        setSales( response.data );
        // Assuming the API returns pagination info, adjust as needed
        setTotalPages( response.pagination?.total || 0 );

      } else {
        setError( response.message || 'Failed to fetch sales' );
        setToast( {
          open: true,
          message: response.message || 'Failed to fetch sales',
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Error fetching sales:', error );
      setError( 'Error fetching sales' );
      setToast( {
        open: true,
        message: 'Error fetching sales',
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  };
  const handleChangePage = ( event: unknown, newPage: number ) => {
    setPage( newPage );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage( parseInt( event.target.value, 10 ) );
    setPage( 0 ); // ✅ reset page
  };

  const getStatusColor = ( status: string ) => {
    switch ( status ) {
      case 'COMPLETED':
      case 'completed':
      case 'BUYER_SATISFIED':
      case 'PAYMENT_COMPLETED':
        return 'success';
      case 'PENDING':
      case 'pending':
      case 'OFFER_CREATED':
      case 'OFFER_SENT':
      case 'OFFER_ACCEPTED':
      case 'SELLER_BOOKED_LOCKER':
      case 'SELLER_EXTENDED_LOCKER_TIME':
      case 'SELLER_PLACED_ITEM':
      case 'BUYER_PICKED_ITEM':
        return 'warning';
      case 'CANCELLED':
      case 'cancelled':
      case 'FAILED':
      case 'OFFER_DECLINED':
      case 'BUYER_UNSATISFIED':
      case 'BUYER_ORDER_CANCELLED':
      case 'SELLER_ORDER_CANCELLED':
      case 'OFFER_FAILED':
      case 'OFFER_FAILED_BY_SELLER':
        return 'error';
      case 'SUPPORT_PICKED_ITEM_FROM_LOCKER':
      case 'ITEM_PICKED_BY_SELLER_FROM_SUPPORT':
      case 'ITEM_PICKED_BY_SUPPORT_FROM_BUYER':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = ( status: string | null ) => {
    if ( !status ) return 'default';

    switch ( status.toLowerCase() ) {
      case 'paid':
      case 'payment_completed':
        return 'success';
      case 'pending':
      case 'payment_in_escrow':
        return 'warning';
      case 'refunded':
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleOrderClick = ( saleId: number ) => {
    navigate( `/orders/${ saleId }` );
  };

  const filteredSales = sales;

  const handleCloseToast = () => {
    setToast( { ...toast, open: false } );
  };

  if ( !userId ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Alert severity="error">
          No user ID provided. Please navigate from the user management screen.
        </Alert>
      </Container>
    );
  }

  if ( loading && sales.length === 0 ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Box sx={ {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        } }>
          <CircularProgress size={ 60 } />
          <Typography variant="h6" color="text.secondary">
            Loading sales data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={ false } sx={ { py: 4, width: '100%' } }>
      <Slide direction="down" in={ true } timeout={ 600 }>
        <Box>
          {/* Header Section */ }
          <Box sx={ { mb: 4 } }>
            <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mb: 2 } }>
              <Box sx={ {
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              } }>
                <StorefrontIcon sx={ { fontSize: 28 } } />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  { t( 'users.sales.title' ) }
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  View and manage user sales transactions
                </Typography>
              </Box>
            </Stack>

            <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 } }>
              <Chip
                icon={ <TrendingUpIcon /> }
                label={ `${ filteredSales.length } sales` }
                color="primary"
                variant="outlined"
              />
            </Box>

            {/* Search and Filter Section */ }
            <Grid container spacing={ 2 } sx={ { mb: 3 } }>
              <Grid item xs={ 12 } md={ 6 }>
                <TextField
                  fullWidth
                  placeholder="Search by title, buyer name, or ID..."
                  value={ searchTerm }
                  onChange={ ( e ) => {
                    setSearchTerm( e.target.value );
                    setPage( 0 ); // ✅ reset pagination
                  } }

                  InputProps={ {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  } }
                  sx={ {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'background.paper'
                    }
                  } }
                />
              </Grid>
              <Grid item xs={ 12 } md={ 6 }>
                <FormControl fullWidth>
                  <InputLabel>{ t( 'common.status' ) }</InputLabel>
                  <Select
                    value={ statusFilter }
                    label={ t( 'common.status' ) }
                    onChange={ ( e ) => {
                      setStatusFilter( e.target.value );
                      setPage( 0 ); // ✅ reset page
                    } }

                    sx={ {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'background.paper'
                      }
                    } }
                  >
                    <MenuItem value="all">{ t( 'common.all' ) }</MenuItem>
                    {/* <MenuItem value="all">{ "All" }</MenuItem> */ }

                    <MenuItem value="OFFER_FAILED_LOCKER_OCCUPIED">{ "OFFER_FAILED_LOCKER_OCCUPIED" }</MenuItem>
                    <MenuItem value="OFFER_FAILED_BY_SELLER">{ "OFFER_FAILED_BY_SELLER" }</MenuItem>

                    <MenuItem value="ITEM_NOT_SUBMIT_TO_SUPPORT_TIME_EXPIRED">
                      { "ITEM_NOT_SUBMIT_TO_SUPPORT_TIME_EXPIRED" }
                    </MenuItem>

                    <MenuItem value="SUPPORT_PICKED_ITEM_FROM_LOCKER">
                      { "SUPPORT_PICKED_ITEM_FROM_LOCKER" }
                    </MenuItem>

                    <MenuItem value="BUYER_ORDER_CANCELLED">
                      { "BUYER_ORDER_CANCELLED" }
                    </MenuItem>

                    <MenuItem value="SELLER_SATISFIED">
                      { "SELLER_SATISFIED" }
                    </MenuItem>

                    <MenuItem value="OFFER_BOOKING_TIME_EXPIRED">
                      { "OFFER_BOOKING_TIME_EXPIRED" }
                    </MenuItem>

                    <MenuItem value="OFFER_ACCEPTANCE_TIME_EXPIRED">
                      { "OFFER_ACCEPTANCE_TIME_EXPIRED" }
                    </MenuItem>

                    <MenuItem value="ITEM_RETURN_TO_SELLER_AFTER_INVESTIGATION">
                      { "ITEM_RETURN_TO_SELLER_AFTER_INVESTIGATION" }
                    </MenuItem>

                    <MenuItem value="ITEM_RETURN_TO_BUYER_AFTER_INVESTIGATION">
                      { "ITEM_RETURN_TO_BUYER_AFTER_INVESTIGATION" }
                    </MenuItem>

                    <MenuItem value="OFFER_DECLINED">
                      { "OFFER_DECLINED" }
                    </MenuItem>

                    <MenuItem value="SELLER_PLACED_ITEM">{ "SELLER_PLACED_ITEM" }</MenuItem>
                    <MenuItem value="OFFER_SENT">{ "OFFER_SENT" }</MenuItem>
                    <MenuItem value="BUYER_UNSATISFIED">{ "BUYER_UNSATISFIED" }</MenuItem>
                    <MenuItem value="SELLER_PICKED_ITEM">{ "SELLER_PICKED_ITEM" }</MenuItem>
                    <MenuItem value="BUYER_ORDER_CANCELLED">{ "BUYER_ORDER_CANCELLED" }</MenuItem>
                    <MenuItem value="ITEM_PICKED_BY_SUPPORT_FROM_BUYER">{ "ITEM_PICKED_BY_SUPPORT_FROM_BUYER" }</MenuItem>
                    <MenuItem value="SELLER_BOOKED_LOCKER">{ "SELLER_BOOKED_LOCKER" }</MenuItem>
                    <MenuItem value="BUYER_SATISFIED">{ "BUYER_SATISFIED" }</MenuItem>
                    <MenuItem value="SELLER_BOOKED_LOCKER">{ "SELLER_BOOKED_LOCKER" }</MenuItem>
                    <MenuItem value="OFFER_FAILED_BY_SELLER">{ "OFFER_FAILED_BY_SELLER" }</MenuItem>
                    <MenuItem value="PAYMENT_COMPLETED">{ "PAYMENT_COMPLETED" }</MenuItem>
                    <MenuItem value="SELLER_ORDER_CANCELLED">{ "SELLER_ORDER_CANCELLED" }</MenuItem>
                    <MenuItem value="failed">{ "Failed Order" }</MenuItem>
                    <MenuItem value="successfull">{ "Successfull Order" }</MenuItem>
                    <MenuItem value="active">{ "Active Order" }</MenuItem>

                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Results Section */ }
          { error ? (
            <Alert severity="error" sx={ { mb: 2 } }>
              { error }
            </Alert>
          ) : (
            <>
              <Paper
                elevation={ 0 }
                sx={ {
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                } }
              >
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={ {
                        backgroundColor: 'primary.main',
                        '& th': {
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }
                      } }>
                        <TableCell align="center">
                          ID
                        </TableCell>
                        <TableCell align="left">
                          { t( 'Title' ) }
                        </TableCell>
                        <TableCell align="left">
                          { t( 'users.sales.buyerName' ) }
                        </TableCell>
                        <TableCell align="center">
                          { t( 'orders.status' ) }
                        </TableCell>
                        <TableCell align="center">
                          { t( 'users.sales.price' ) }
                        </TableCell>
                        <TableCell align="center">
                          { t( 'users.sales.saleDate' ) }
                        </TableCell>

                        <TableCell align="center">
                          { t( 'orders.payment_status' ) }
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      { filteredSales.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={ 7 }>
                            <Box sx={ {
                              textAlign: 'center',
                              py: 8,
                              color: 'text.secondary'
                            } }>
                              <StorefrontIcon sx={ { fontSize: 64, mb: 2, opacity: 0.5 } } />
                              <Typography variant="h6" sx={ { mb: 1 } }>
                                No sales found
                              </Typography>
                              <Typography variant="body2">
                                { searchTerm || statusFilter !== 'all' ? 'Try adjusting your search criteria' : 'No sales data available' }
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredSales.map( ( sale ) => (
                          <TableRow
                            key={ sale.id }
                            sx={ {
                              '&:hover': {
                                backgroundColor: ( theme ) => alpha( theme.palette.primary.main, 0.04 )
                              },
                              transition: 'background-color 0.2s ease'
                            } }
                          >
                            <TableCell align="center">
                              <Link
                                component="button"
                                variant="body2"
                                onClick={ () => handleOrderClick( sale.id ) }
                                sx={ {
                                  textDecoration: 'none',
                                  fontWeight: 600,

                                } }
                              >
                                { sale.id }
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" gutterBottom>
                                { sale.title }
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" >
                                { `${ sale.buyer.firstName } ${ sale.buyer.lastName }` }
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={ sale.status }
                                color={ getStatusColor( sale.status ) as any }
                                size="small"
                                sx={ { fontWeight: 600 } }
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="subtitle2" >
                                ${ parseFloat( sale.price ).toFixed( 2 ) }
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" color="text.secondary">
                                { new Date( sale.createdAt ).toLocaleDateString() }
                              </Typography>
                            </TableCell>

                            <TableCell align="center">
                              <Chip
                                label={ sale.paymentStatus || 'N/A' }
                                color={ getPaymentStatusColor( sale.paymentStatus ) as any }
                                size="small"
                                sx={ { fontWeight: 600 } }
                              />
                            </TableCell>
                          </TableRow>
                        ) )
                      ) }
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={ [ 5, 10, 25 ] }
                    component="div"
                    count={ totalPages }          // ✅ backend total
                    rowsPerPage={ rowsPerPage }  // ✅ current limit
                    page={ page }                // ✅ 0-based
                    onPageChange={ handleChangePage }
                    onRowsPerPageChange={ handleChangeRowsPerPage }
                  />

                </TableContainer>

              </Paper>

              {/* { totalPages > 1 && (
                <Box sx={ { display: 'flex', justifyContent: 'center', mt: 3 } }>
                  <Pagination
                    count={ totalPages }
                    page={ page }
                    onChange={ handlePageChange }
                    color="primary"
                    size="large"
                  />
                </Box>
              ) } */}
            </>
          ) }
        </Box>
      </Slide>

      <Snackbar
        open={ toast.open }
        autoHideDuration={ 6000 }
        onClose={ handleCloseToast }
        anchorOrigin={ { vertical: 'top', horizontal: 'right' } }
      >
        <Alert onClose={ handleCloseToast } severity={ toast.severity } sx={ { width: '100%' } }>
          { toast.message }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MySales;
